import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import MenuItem from '@material-ui/core/MenuItem';
import { Loader, usePrintDispatch } from '@mummssoftware/common-ui';
import { CPPGlobalContext } from '@mummssoftware/global-context/CPPGlobalContext';
import { Printing } from '@mummssoftware/utils';
import { ANSWERED } from '@mummssoftware/utils/constants';
import { find } from 'lodash-es';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { Context } from '../../context';
import defaults from '../../translations/en.json';
import { Print, Resolved, SET_ERROR } from '../Context/constants';
import { CPPContext } from '../Context/CPPContext';

type PrintSingleCPPProps = {
  patientCarePlanProblem: mumms.patientCarePlanProblem;
};

export const PrintSingleCPP = ({
  patientCarePlanProblem,
}: PrintSingleCPPProps) => {
  const intl = useIntl();

  const { answeredSubmittedCPPGlobal, cppNotesGlobal } = useContext(
    CPPGlobalContext,
  );
  const { agency, currentUser, patient, observation, keycloak } = useContext(
    Context,
  );
  const { dispatch } = useContext(CPPContext);

  const [printPlan, setPrintPlan] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const printingService = useMemo(
    () =>
      new Printing({
        url: window.env.hosts.printingService,
        keycloak,
        agency,
        currentUser,
      }),
    [keycloak, agency, currentUser],
  );

  const getPrintPromise = useCallback(
    (faxInstructions: any, emailInstructions: any) =>
      printingService.printCarePlanProblems(
        [printPlan],
        patient!,
        cppNotesGlobal as any,
        faxInstructions,
        emailInstructions,
      ),
    [printingService, printPlan, patient, cppNotesGlobal],
  );

  const { print, fax, email } = usePrintDispatch(
    getPrintPromise,
    () => setLoading(true),
    () => setLoading(false),
  );

  useEffect(() => {
    if (patientCarePlanProblem.status === Resolved) {
      getResolvedPlanProblem();
    } else {
      const printToPlan: any = answeredSubmittedCPPGlobal
        ? find(
            answeredSubmittedCPPGlobal,
            (plan) => plan.id === patientCarePlanProblem.id,
          )
        : null;
      setPrintPlan(printToPlan);
    }
  }, []);

  const getResolvedPlanProblem = async () => {
    const mode: mumms.CPPMode = ANSWERED;
    await observation
      .getPatientCarePlanProblemByIdv2(patientCarePlanProblem.id, mode, agency)
      .then(async (res: any) => {
        if (!(res instanceof Response)) {
          setPrintPlan(res);
        } else {
          dispatch({
            type: SET_ERROR,
            payload: intl.formatMessage(defaults.errorMsg, {
              status: res.status,
              cpp: patientCarePlanProblem.careplanproblemname,
            }),
          });
        }
      })
      .catch((err) => {
        dispatch({
          type: SET_ERROR,
          payload: intl.formatMessage(defaults.errorMsg, {
            status: '',
            cpp: patientCarePlanProblem.careplanproblemname,
          }),
        });
      });
  };

  const guard = (fn: () => void) => () => {
    if (!keycloak || !patient || !printPlan) return;
    fn();
  };

  return (
    <Fragment>
      {loading &&
        createPortal(
          <Loader />,
          document.getElementById('portal-container') || document.body,
        )}
      {printPlan ? (
        <Fragment>
          <MenuItem key={Print} onClick={guard(print)}>
            {Print}
          </MenuItem>
          <MenuItem onClick={guard(fax)}>Fax</MenuItem>
          <MenuItem onClick={guard(email)}>Email</MenuItem>
        </Fragment>
      ) : (
        <Fragment>
          <MenuItem>{defaults.loading}</MenuItem>
        </Fragment>
      )}
    </Fragment>
  );
};
